import React, {useEffect, useMemo} from 'react';
import {Nav, NavItem, NavLink, TabContent, TabPane} from "reactstrap";
import PropTypes from "prop-types";
import CoverLetterTab from "./CoverLetterTab";
import MatchScoreTab from "./MatchScoreTab";
import {useQueryClient} from '@tanstack/react-query';
import {useAuth} from "../../providers/AuthProvider";
import MessagesTab from "../proposals/MessagesTab";
import MeetingsTab from "./MeetingsTab";
import UserImageCircle from "../../components/UserImageCircle";
import {useProject} from "../../providers/ClientProjectProvider";
import {useMedia, usePrevious} from "react-use";
import {Link, useLocation, useNavigate, useSearchParams} from "react-router-dom";
import Team from "../../models/Team";
import TeamProposalTab from './TeamProposalTab';
import {useAPI} from "../../config/API";


const TeamCandidateProposal = (props) => {
    const {project} = useProject();
    const {auth} = useAuth();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const isLargeScreenWidth = useMedia('(max-width: 1024px)'); // 1024 matches tailwind lg size

    useEffect(() => {
        if (!isLargeScreenWidth && selectedTab === 'messages') {
            setSelectedTab(tabs[0]);
        }
    }, [isLargeScreenWidth])

    const updateCandidate = async(candProps = {}) => {
        let update = Team.make({
            ...props.team,
            ...candProps
        })
        // await props.position.resetCandidateQueryDataForTalent(update);

        if(props.onUpdated) props.onUpdated(update);
    }

    const {
        data,
    } = useAPI(['markProposalAsViewed', {
        client_id: auth.clientOrg?.()?.id,
        project_id: project?.id,
        candidate_id: props.team?.id
    }], {
        // enabled: !!team?.id,
        onSuccess: async(data) => {
            let viewedAt = props.team.job_candidate.proposal_updated_at;
            let marked = TB.moment()
            // if(viewedAt && TB.moment(viewedAt).isAfter(marked)) {
            //     marked = TB.moment(viewedAt);
            // }
            let candidateJobData = TB.set(
                {...props.team},
                'job_candidate.last_viewed_at',
                marked.toDate()
            );

            await updateCandidate(candidateJobData);
        }
    })

    const canRequestTalentInterviewForProject = auth.canRequestTalentInterviewForProject(props.project);

    const interviewBadge = () => {
        if (!canRequestTalentInterviewForProject) {
            return <i className="fa fa-lock" style={{fontSize: 12}}/>
        }

        if (project.is_on_hold || (!props.team.is_assigned)) {
            return null;
        }

        if(!props.team.has_proposed_interview &&
            !props.team.upcoming_meetings_count &&
            !props.team.proposed_meetings_count && !props.team.latest_meeting_update_at
        ) {
            return (
                <span className="pulse-indicator pulse-relative">
                    <span className="blob info"></span>
                </span>
            )
        }

        // Show badge if interview was updated within the past hour
        if(props.team.hasInterviewUpdateSinceLastViewed()) {
            return <i className="fa fa-exclamation-circle text-danger" style={{fontSize: 12}}/>
        }

        return null;
    }

    const messageBadge = () => {
        if (!!project.unread_messages_count) {
            return (
                <span className="pulse-indicator pulse-relative">
                    <span className="blob info"></span>
                </span>
            )
        }
        return null;
    }

    const tabs = [
        "cover-letter",
        "availability-rate",
        "match-summary",
        "interviews",
        "team-proposal",
    ]


    const previousLocationSearch = usePrevious(searchParams.get('tabView'));

    const selectedTab = useMemo(() => {
        if (props.selectedTab) return props.selectedTab;
        let tab;
        if(searchParams.has('tabView')) {
            tab =  TB.find(tabs, tab => TB.slugify(tab) === searchParams.get('tabView'));
        }
        if(!tab) {
            tab = previousLocationSearch || TB.first(tabs)
        }
        return tab;
    }, [location.search, props.selectedTab]);


    const setSelectedTab = (tab) => {
        if(props.onTabChange) {
            return props.onTabChange(tab)
        }
        let params = new URLSearchParams(searchParams);
        params.set('tabView', tab);
        return setSearchParams(params, {replace:true})
    };


    const detailsLink = useMemo(() => {
        let path = location.pathname;
        const team = props.team;
        let params = {}

        if (project?.id) {
            params.project_id = project?.id;
        }

        let state = {
            ...(location.state || {}),
            matching: props.team?.matching,
            linkParams: {
                title: props.backBtnTitle || 'Back',
                back: true
                // path: {
                //     ...location,
                //     pathname: location.pathname
                // }
            }
        }

        return {
            pathname: path + `/team-profiles/${team.id}/match`,
            state
        }
    }, [location.pathname, props.backBtnTitle])


    const canInviteTalentForProject = project && auth?.canInviteTalentForProject(project)
    const canAssignTalentForProject = project && auth?.canAssignTalentForProject(project)

    const runProjectTalentRequest = async(method, successKey) => {
        let candidate = Team.make({...props.team});

        let wasSuccess = await props.onRequest(async () => {
            let updatedCandidate = await candidate[method](
                project
            )

            if (!updatedCandidate) {
                return false;
            }

            let callback = 'on' + TB.upperFirst(method);
            if (props[callback]) {
                await props[callback](updatedCandidate);
            }

            return true;
        });

        if (wasSuccess) {
            await TB.sysAlert(successKey);
        }
    }

    const removeFromCandidates = async() => {
        if (!canInviteTalentForProject) {
            return TB.showPermissionError();
        }
        return runProjectTalentRequest(
            'removeFromCandidates', 'byo.remove_from_candidate_button_clicked'
        );
    }

    const addToCandidates = async() => {
        return runProjectTalentRequest(
            'addToCandidates', 'byo.add_as_candidate_button_clicked'
        );
    }

    const assignCandidate = async() => {

        if (!props.team.isShortlisted()) {
            return TB.sysAlert('byo.cant_select_uninterested_talent_for_role');
        }

        // return TB.event("Modal@show", {
        //     modalClass: "assign-candidate-modal",
        //     content: (
        //         <AssignCandidateModal
        //             position={position}
        //             candidate={props.candidate}
        //             onConfirm={async() => {
        //                 await runProjectTalentRequest('assignTo',
        //                     !TB.has(TB.storedPreferences(), `hide_tips.byo.select_for_role_button_clicked_tip`)
        //                         ? 'byo.select_for_role_button_clicked_tip'
        //                         : 'byo.select_for_role_button_clicked'
        //                 )
        //             }}
        //         />
        //     )
        // })
    }

    const unassignCandidate = () => {
        if (project.hasRequestedSOW()) {
            return TB.toast("Can't reopen after SOW has already been requested!", {theme: 'danger'})
        }
        if (project.assigned_team_candidate?.id !== props.team?.id) {
            return;
        }

        return runProjectTalentRequest(
            'unassignFrom', 'byo.removed_for_role_button_clicked'
        );
    }


    return (
        <div className="proposal-container candidate-proposal">

            {!props.minimal && (
                <div className="proposal-header">
                    {props.onClose && (
                        <button className="btn btn-link-primary proposal-back-btn" onClick={props.onClose}>
                            <i className="fa fa-angle-left mr-2"/>
                            {props.backBtnTitle}
                        </button>
                    )}
                    <h2 className="mb-0">
                        Team Candidate Proposal

                        {props.team?.talent_proposal_updated_at && (
                            <small className="ml-3">
                                Last updated: {TB.moment(props.team?.talent_proposal_updated_at).fromNow()}
                            </small>
                        )}
                    </h2>
                </div>
            )}

            <div className="proposal-info">
                {!props.minimal && (
                    <div className="candidate-container">

                        <div className="candidate-info flex-col md:flex-row items-center">
                            <UserImageCircle
                                user={props.team.positions[1]} size={100}
                                hideTooltip={true}
                                className="mr-1 img-grayscale"/>
                            <div className="flex flex-col items-center md:items-start gap-12">
                                <div className="flex flex-col items-center md:items-start gap-6 md:gap-0">
                                    <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
                                        <h5>
                                            {props.team.name}
                                        </h5>
                                        <p className="candidate-background">
                                            <i className="fa fa-info-circle"/>
                                            {/*{props.team?.job_title}*/}
                                        </p>


                                        {props.team?.location && (
                                            <p className="candidate-background">
                                                <i className="fa fa-map-marker-alt"/>
                                                {props.team?.location.city}, {props.team?.location.state}
                                            </p>
                                        )}

                                    </div>
                                    {props.team?.bio && (
                                        <p className="truncating-text team-bio text-center md:text-left">
                                            {props.team?.fullBio()}
                                        </p>
                                    )}
                                </div>

                                <Link className="details-link"
                                      to={detailsLink} state={{backBtnTitle: props.backBtnTitle}}>
                                    See Details
                                </Link>
                            </div>
                        </div>

                        {/*<CandidateProposalActions*/}
                        {/*    positionAssignable={props.positionAssignable}*/}
                        {/*    candidate={props.candidate}*/}
                        {/*    assigned={position?.assigned_candidate}*/}
                        {/*    onUnassignCandidate={unassignCandidate}*/}
                        {/*    onAssignCandidate={assignCandidate}*/}
                        {/*    onAddCandidate={addToCandidates}*/}
                        {/*    onRemoveCandidate={removeFromCandidates}*/}
                        {/*    isDisabled={props.isDisabled}*/}
                        {/*    canAssignTalentForProject={canAssignTalentForProject}*/}
                        {/*    canInvite={canInviteTalentForProject}/>*/}
                    </div>
                )}
                <Nav tabs>
                    <NavItem>
                        <NavLink
                            active={selectedTab === "cover-letter"}
                            className={TB.mapClass({
                                "clickable": selectedTab !== "cover-letter"
                            })}
                            onClick={() => setSelectedTab("cover-letter")}
                        >
                            Cover Letter
                        </NavLink>
                    </NavItem>

                    <NavItem>
                        <NavLink
                            className={TB.mapClass({
                                "active": selectedTab === "match-summary",
                                "clickable": selectedTab !== "match-summary"
                            })}
                            onClick={() => setSelectedTab("match-summary")}
                        >
                            Match Summary
                        </NavLink>
                    </NavItem>

                    {props.team && (
                        <>
                            <NavItem>
                                <NavLink
                                    className={TB.mapClass("relative flex flex-row items-center", {
                                        "active": selectedTab === "interviews",
                                        "clickable": selectedTab !== "interviews"
                                    })}
                                    style={{gap: 8}}
                                    onClick={() => setSelectedTab("interviews")}
                                >
                                    Interviews
                                    {interviewBadge()}
                                </NavLink>
                            </NavItem>

                            <NavItem className="lg:hidden">
                                <NavLink
                                    className={TB.mapClass("relative flex flex-row items-center", {
                                        "active": selectedTab === "messages",
                                        "clickable": selectedTab !== "messages"
                                    })}
                                    style={{gap: 8}}
                                    onClick={() => setSelectedTab("messages")}
                                >
                                    Messages
                                    {messageBadge()}
                                </NavLink>
                            </NavItem>

                            <NavItem>
                                <NavLink
                                    className={TB.mapClass("relative flex flex-row items-center", {
                                        "active": selectedTab === "team-proposal",
                                        "clickable": selectedTab !== "team-proposal"
                                    })}
                                    style={{gap: 8}}
                                    onClick={() => setSelectedTab("team-proposal")}
                                >
                                    Team Proposal
                                </NavLink>
                            </NavItem>
                        </>
                    )}
                </Nav>
            </div>

            <div className="proposal-overview">

                <div className="proposal-tabs-section">

                    <TabContent activeTab={selectedTab} className="proposal-tab-content">
                        <TabPane tabId="cover-letter">
                            {selectedTab === "cover-letter" && (
                                <CoverLetterTab candidate={props.team}/>
                            )}
                        </TabPane>
                        {/*<TabPane tabId="availability-rate">*/}
                        {/*    {selectedTab === "availability-rate" && (*/}
                        {/*        <AvailabilityRateTab candidate={props.team} />*/}
                        {/*    )}*/}
                        {/*</TabPane>*/}

                        <TabPane tabId="match-summary">
                            {selectedTab === "match-summary" && (
                                <MatchScoreTab
                                    candidate={props.team}
                                    projectId={project?.id}/>
                            )}

                        </TabPane>
                        {props.team && (
                            <>
                                <TabPane tabId="interviews">
                                    {selectedTab === "interviews" && (
                                        <MeetingsTab
                                            candidate={props.team}
                                        />
                                    )}
                                </TabPane>

                                <TabPane tabId="messages">
                                    {selectedTab === "messages" && (
                                        <div className="proposal-messages candidate-messages m-0">

                                            <MessagesTab
                                                hasPermission={auth.canRequestTalentInterviewForProject(project)}
                                                candidate={props.team}
                                                project={project}
                                                onFetchSuccess={() => {

                                                    // let viewedAt = props.team.job_candidate.proposal_updated_at;
                                                    // let marked = TB.moment()
                                                    // // if(viewedAt && TB.moment(viewedAt).isAfter(marked)) {
                                                    // //     marked = TB.moment(viewedAt);
                                                    // // }
                                                    // let candidateJobData = TB.set(
                                                    //     {...props.team},
                                                    //     'job_candidate.last_viewed_client_byo_position_candidate_proposal_at',
                                                    //     marked.toDate()
                                                    // );
                                                    // candidateJobData = TB.set(candidateJobData,
                                                    //     'job_candidate.unread_messages_count',
                                                    //     0
                                                    // )
                                                    // return updateCandidate(candidateJobData)
                                                }}
                                                hasRequestedSOW={props.project?.hasRequestedSOW()}
                                            />
                                        </div>
                                    )}
                                </TabPane>

                                <TabPane tabId="team-proposal">
                                    {selectedTab === "team-proposal" && (
                                        <TeamProposalTab team={props.team} backBtnTitle={props.backBtnTitle} />
                                    )}
                                </TabPane>
                            </>

                        )}
                    </TabContent>

                </div>

                {props.team && (
                    <div className="proposal-messages candidate-messages hidden lg:flex">
                        <MessagesTab
                            hasPermission={auth.canRequestTalentInterviewForProject(project)}
                            candidate={props.team}
                            project={project}
                            onFetchSuccess={() => {

                                // let viewedAt = props.team.job_candidate.proposal_updated_at;
                                // let marked = TB.moment()
                                // // if(viewedAt && TB.moment(viewedAt).isAfter(marked)) {
                                // //     marked = TB.moment(viewedAt);
                                // // }
                                // let candidateJobData = TB.set(
                                //     {...props.team},
                                //     'job_candidate.last_viewed_client_byo_position_candidate_proposal_at',
                                //     marked.toDate()
                                // );
                                // candidateJobData = TB.set(candidateJobData,
                                //     'job_candidate.unread_messages_count',
                                //     0
                                // )
                                // return updateCandidate(candidateJobData)
                            }}
                            hasRequestedSOW={props.project?.hasRequestedSOW()}
                        />
                    </div>
                )}

            </div>
        </div>
    )
};

TeamCandidateProposal.propTypes = {
    isDisabled: PropTypes.bool,
    canRemove: PropTypes.any,
    onRemove: PropTypes.any,
    onExit: PropTypes.any,
    candidate: PropTypes.any,
    canRequestInterview: PropTypes.any,
    onRequestInterview: PropTypes.any,
    isSelected: PropTypes.any,
    canBeSelected: PropTypes.any,
    onSelected: PropTypes.any,
    backBtnTitle: PropTypes.any
};
const styles = {
    favoriteBtn: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'flex-end'
    },

    archiveBtn: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'flex-start'
    },
    inviteLockIcon: {
        position: 'absolute',
        fontSize: 13,
        right: -18,
    }
};

export default TeamCandidateProposal;